import { cn } from "@/lib/utils"
import { Card, CardContent } from "@/components/ui/card"
import { WidgetCardHeader } from "@/components/chat/WidgetCardHeader"
import {
  TaskSummaryContent,
  type TaskContentDocument,
  type TaskNoteTone,
  type TaskStop,
} from "@/components/chat/TaskSummaryContent"

/**
 * The data a WidgetTaskSummary shows. Presentational only — the caller owns
 * where these values come from and what the links do.
 */
export type WidgetTaskSummaryData = {
  /** Order name / reference — the card title, e.g. "ZL-2847". */
  taskRef: string
  /** Date shown above the title, e.g. "śr., 2 wrz 2026". */
  dateLabel?: string
  pickup: TaskStop
  delivery: TaskStop
  /** How long the order takes end to end, in minutes. */
  durationMinutes: number
  /** Total distance for the order, in kilometres. */
  distanceKm: number
  /** Road tolls for the order, in euros. */
  tollsEur: number
  /** Duration vs. the plan, in minutes. Negative = shorter than expected. */
  durationDeltaMinutes?: number
  /** Distance vs. the plan, in km. Negative = shorter than expected. */
  distanceDeltaKm?: number
  /** Road tolls vs. the plan, in euros. Negative = cheaper than expected. */
  tollsDeltaEur?: number
  /** Documents attached to the order (CMR, POD, …) — one or two, each opens
   * on click. Shown in their own section under the values. */
  documents?: TaskContentDocument[]
  /** Free-text line about how it went — "Dostarczono na czas", "Opóźnienie 20 min", … */
  note?: string
  /** Tone for `note` + its icon. Defaults to "neutral". */
  noteTone?: TaskNoteTone
}

export type WidgetTaskSummaryProps = WidgetTaskSummaryData & {
  /** Opens the order's document (a PDF scan). The title becomes a link when set. */
  onOpenDocument?: () => void
  /** Jumps the driver chat to the conversation about this order — a top-right
   * icon button ("Chat with a driver"). */
  onChatWithDriver?: () => void
  /** Opens the order in the widget panel (the "tools" window). */
  onOpenInPanel?: () => void
  className?: string
}

/**
 * WidgetTaskSummary — an "order at a glance" card the Slick AI agent drops
 * into the conversation: pickup → delivery, how long it takes, how far it
 * runs and its road tolls (each against plan), optionally its documents, and
 * a line on how it went. A white shadcn card. The title links to the order's
 * PDF scan; the top-right icons jump the driver chat to this order's
 * conversation and open the order in the widget panel.
 */
export const WidgetTaskSummary = ({
  taskRef,
  dateLabel,
  pickup,
  delivery,
  durationMinutes,
  distanceKm,
  tollsEur,
  durationDeltaMinutes,
  distanceDeltaKm,
  tollsDeltaEur,
  documents,
  note,
  noteTone = "neutral",
  onOpenDocument,
  onChatWithDriver,
  onOpenInPanel,
  className,
}: WidgetTaskSummaryProps) => (
  <Card className={cn("w-full max-w-md shadow-none", className)}>
    <WidgetCardHeader
      title={taskRef}
      dateLabel={dateLabel}
      onOpenDocument={onOpenDocument}
      onChatWithDriver={onChatWithDriver}
      onOpenInPanel={onOpenInPanel}
    />

    <CardContent className="p-4 pt-0">
      <TaskSummaryContent
        pickup={pickup}
        delivery={delivery}
        durationMinutes={durationMinutes}
        distanceKm={distanceKm}
        tollsEur={tollsEur}
        durationDeltaMinutes={durationDeltaMinutes}
        distanceDeltaKm={distanceDeltaKm}
        tollsDeltaEur={tollsDeltaEur}
        documents={documents}
        note={note}
        noteTone={noteTone}
      />
    </CardContent>
  </Card>
)

export default WidgetTaskSummary
